/**
 * Parcours assistant d’envoi (5 étapes) — règles partagées admin / agent.
 * Le dentiste conserve son flux existant (hub, liens dédiés).
 */

import {
  getMissingClinicalPhotoKeys,
  hasAllRequiredClinicalPhotos,
} from '../case-dentist/scanUploadConfig';

export const ADMIN_ASSISTANT_ROLES = new Set(['administrator', 'agent_administratif']);

const SUBMITTED_PHASE_STATUSES = new Set(['submitted', 'completed']);

/**
 * @param {Array<{ group?: string|number, phase_order?: number, status?: string }>} phases
 */
export function getSubmissionPhaseFromTimeline(phases = []) {
  const list = Array.isArray(phases) ? phases : [];
  return (
    list.find(
      (p) => String(p.group ?? '') === '1' && Number(p.phase_order) === 1,
    ) ?? null
  );
}

/**
 * @param {string|null|undefined} status
 */
export function isSubmissionPhaseSubmitted(status) {
  if (!status) return false;
  return SUBMITTED_PHASE_STATUSES.has(String(status));
}

/**
 * @param {string | string[] | null | undefined} roleOrRoles
 */
export function normalizeRoleTypes(roleOrRoles) {
  if (Array.isArray(roleOrRoles)) {
    return roleOrRoles.filter((r) => typeof r === 'string' && r.trim());
  }
  if (typeof roleOrRoles === 'string' && roleOrRoles.trim()) return [roleOrRoles];
  return [];
}

/**
 * @param {string | string[] | null | undefined} roleOrRoles
 */
export function isAdminAssistantRole(roleOrRoles) {
  const roles = normalizeRoleTypes(roleOrRoles);
  return roles.some((r) => ADMIN_ASSISTANT_ROLES.has(r));
}

/**
 * Admin / agent : fiche commande bloquée tant que la phase soumission n’est pas envoyée.
 * @param {{
 *   role?: string | null,
 *   roleTypes?: string | string[],
 *   submissionPhaseStatus?: string | null,
 * }} opts
 */
export function mustCompleteAssistantBeforeCaseDetail(opts = {}) {
  const roles = normalizeRoleTypes(opts.roleTypes);
  const role = opts.role;
  const isAdminSide =
    (role && ADMIN_ASSISTANT_ROLES.has(role)) ||
    roles.some((r) => ADMIN_ASSISTANT_ROLES.has(r));

  if (!isAdminSide) return false;
  return !isSubmissionPhaseSubmitted(opts.submissionPhaseStatus);
}

/**
 * @param {Array<{ group?: string, phase_order?: number, status?: string }>} phases
 * @param {string | string[] | null | undefined} roleOrRoles
 */
export function shouldOpenCaseViaAssistant(caseId, phases, roleOrRoles) {
  if (!caseId) return false;
  if (!isAdminAssistantRole(roleOrRoles)) return false;
  const submission = getSubmissionPhaseFromTimeline(phases);
  return !isSubmissionPhaseSubmitted(submission?.status);
}

/**
 * @param {string} caseId
 */
export function getCaseAssistantPath(caseId) {
  return `/case/${caseId}/assistant`;
}

function hasUploadedScans(files = []) {
  const list = Array.isArray(files) ? files : [];
  const upper = list.some(
    (f) =>
      f.category === 'scans' &&
      String(f.filePath || '').includes('-upper.stl'),
  );
  const lower = list.some(
    (f) =>
      f.category === 'scans' &&
      String(f.filePath || '').includes('-lower.stl'),
  );
  return upper || lower;
}

function buildUploadedPhotosByKey(files = []) {
  const map = {};
  for (const f of files) {
    if (f.category === 'photos' && f.photoKey) map[f.photoKey] = f;
  }
  return map;
}

function hasPrescriptionBasics(caseRow) {
  if (!caseRow) return false;
  const arch = caseRow.arch_to_treat ?? caseRow.archToTreat;
  const treatment = caseRow.treatment_type ?? caseRow.treatmentType;
  return Boolean(arch && treatment);
}

/**
 * Reprend l’étape assistant (1–5) selon l’état du dossier.
 * @param {{ files?: Array, caseRow?: object|null }} opts
 * @returns {number} 1..5
 */
export function getAssistantResumeStep({ files = [], caseRow = null } = {}) {
  const uploadedPhotosByKey = buildUploadedPhotosByKey(files);
  const photosOk = hasAllRequiredClinicalPhotos(uploadedPhotosByKey);
  const scansOk = hasUploadedScans(files);
  const packageOk = Boolean(caseRow?.package);
  const prescriptionOk = hasPrescriptionBasics(caseRow);

  if (!scansOk) return 1;
  if (!photosOk) return 2;
  if (!prescriptionOk) return 3;
  if (!packageOk) return 4;
  return 5;
}

/**
 * @param {Array} phases
 */
export function getSubmissionPhaseStatusFromPhases(phases) {
  return getSubmissionPhaseFromTimeline(phases)?.status ?? null;
}
